import axios from "axios";
import { getErrorMessage, isErrorResponse } from "../../lib/status-codes";
import { validationSummary } from "../../lib/validations";
import { bindSelectAllCheckboxToList } from "../../lib/elementUtils";
import { FileRow, FolderRow, ListRowItem } from "./types";
import { LoadingSpinner } from "../../components/loadingSpinner";
import { Breadcrumbs } from "../../components/breadcrumbs";
import { currentPageWithHandler } from "../../lib/urlUtil";
import { DataTablesHelper } from "../../lib/datatables";

export class SdfFileList {

    private dom = {
        parent: document.body,
        fileRows: () => this.dom.parent.querySelectorAll<FileRow>(".table__row[data-file-id]"),
        fileCheckboxes: () => this.dom.parent.querySelectorAll<HTMLInputElement>(".table__row[data-file-id] input[type='checkbox']"),
        folderRows: () => this.dom.parent.querySelectorAll<FolderRow>(".table__row[data-folder-id]"),
        selectAllCheckbox: () => this.dom.parent.querySelector<HTMLInputElement>("input[value='select-all']"),
        selectedFiles: () => this.dom.parent.querySelectorAll<HTMLInputElement>(".table__row[data-file-id] input[type='checkbox']:checked"),
        selectedFolders: () => this.dom.parent.querySelectorAll<HTMLInputElement>(".table__row[data-folder-id] input[type='checkbox']:checked"),
        breadcrumbsWrapper: () => this.dom.parent.querySelector<HTMLElement>(".sdf-breadcrumbs"),
        renameFolderButton: document.querySelector<HTMLElement>(".upload-to-sdf__button--rename")!,
        deleteFolderButton: document.querySelector<HTMLElement>(".upload-to-sdf__button--delete-folder")!,
        renameFolderPopupFields: {
            englishNameInput: document.querySelector<HTMLInputElement>(".upload-to-sdf__rename-folder-popup #RenameFolderViewModel_FolderNameEn")!,
            welshNameInput: document.querySelector<HTMLInputElement>(".upload-to-sdf__rename-folder-popup #RenameFolderViewModel_FolderNameCy")!
        }
    }
    private loadingSpinner: LoadingSpinner;

    private breadcrumbs: Breadcrumbs;

    private isListFileArchive: boolean;

    /**
     * @param parentNode The sdf-file-list root node to bind to
     */
    constructor(parentNode: HTMLElement, isListFileArchive: boolean) {

        this.dom.parent = parentNode;

        this.loadingSpinner = new LoadingSpinner(parentNode);

        this.breadcrumbs = new Breadcrumbs(this.dom.breadcrumbsWrapper())

        const linkParams = new URL(window.location.href);
        const folderId = parseInt(linkParams.searchParams.get("FolderId") ?? "0");

        if (folderId != 0) {
            console.log("Showing buttons")
            this.dom.renameFolderButton?.classList.remove("hidden");
            this.dom.deleteFolderButton?.classList.remove("hidden");
        }
        else {
            this.dom.renameFolderButton?.classList.add("hidden");
            this.dom.deleteFolderButton?.classList.add("hidden");
        }

        this.isListFileArchive = isListFileArchive;

        this.addEventListeners();
    }

    /**
     * Register any event listeners required for the SDF file list.
     * This should be called whenever the HTML content of the file list is replaced
     */
    async addEventListeners() {
        // Select all checkboxes
        bindSelectAllCheckboxToList(this.dom.selectAllCheckbox(), this.dom.fileCheckboxes());

        // Bind folder update behaviour to folder links
        const folderRows = this.dom.folderRows();
        folderRows.forEach((folder) => {
            // Get folderId
            const folderId = parseInt(folder.dataset.folderId);

            // Get the folder link element for that row
            const folderLink = folder.querySelector("a");

            folderLink?.addEventListener("click", async (ev) => {
                // On click, update the folder without reloading the whole page
                ev.preventDefault();
                await this.openFolder(folderId)
                if (this.dom.renameFolderPopupFields.englishNameInput
                    && this.dom.renameFolderPopupFields.welshNameInput
                    && !this.isListFileArchive) {
                    await this.populateRenamePopupInputFields();
                }
                // Block default browser navigation
                return false;
            })
        });

        // Bind breadcrumb links to update the current folder
        this.breadcrumbs = new Breadcrumbs(this.dom.breadcrumbsWrapper());
        const breadcrumbLinks = this.breadcrumbs.dom.links()
        breadcrumbLinks.forEach((breadcrumbLink) => {
            // Get the folder ID from the breadcrumb link
            const linkParams = new URL(breadcrumbLink.href);
            const folderId = parseInt(linkParams.searchParams.get("FolderId") ?? "0");

            breadcrumbLink.addEventListener("click", async (ev) => {
                // On click, update the folder without reloading the whole page
                ev.preventDefault();
                await this.openFolder(folderId);
                if (this.dom.renameFolderPopupFields.englishNameInput
                    && this.dom.renameFolderPopupFields.welshNameInput
                    && !this.isListFileArchive) {
                    await this.populateRenamePopupInputFields();
                }
                // Block default browser navigation
                return false;
            })
        });

    }

    /**
     * Get a list of items that the user currently has selected
     */
    getSelectedItems() {
        const selectedItems: ListRowItem[] = [];

        this.dom.selectedFiles().forEach(fileInput => {
            selectedItems.push({
                id: fileInput.value,
                type: "file"
            })
        })

        this.dom.selectedFolders().forEach(folderInput => {
            selectedItems.push({
                id: folderInput.value,
                type: "folder"
            });
        })

        return selectedItems;
    }

    /**
     * Get a list of the file type checkboxes
     */
    getFileCheckboxes() {
        return this.dom.fileCheckboxes;
    }

    /**
     * Switch the view to a new folder
     */
    async openFolder(folderId: number) {
        try {
            await this.loadingSpinner.startSpinner();
            const url = this.isListFileArchive
                ? `/SoftwareDevelopmentForum/SoftwareDevForum?handler=FileListPartial&folderId=${folderId}`
                : `/SoftwareDevelopmentForum/Upload?handler=FileListPartial&folderId=${folderId}`;

            const response = await axios.get(url);

            if (isErrorResponse(response)) {
                throw response;
            }

            // Update the contents of the file list partial
            this.dom.parent.innerHTML = response.data;
            await this.addEventListeners();

            if (folderId != 0) {
                this.dom.renameFolderButton?.classList.remove("hidden");
                this.dom.deleteFolderButton?.classList.remove("hidden");
            }
            else {
                this.dom.renameFolderButton?.classList.add("hidden");
                this.dom.deleteFolderButton?.classList.add("hidden");
            }

            // Update the URL to reflect the new folder
            const currentLocation = new URL(document.location.href);
            currentLocation.searchParams.set("FolderId", folderId.toString());
            window.history.pushState({}, '', currentLocation.toString());

        } catch (error) {
            validationSummary(
                {
                    body: "",
                    level: "error",
                    title: await getErrorMessage(error)
                }
            )
        } finally {
            DataTablesHelper.SoftwareDevelopmentForum();
            this.loadingSpinner.stopSpinner()
        }
    }

    /**
     * Get current folder name (en and cy) to pre-populate
     * rename input fields
     */
    async populateRenamePopupInputFields() {
        try {
            const url = currentPageWithHandler("CurrentFolder");
            const response = await axios.get(url);

            if (isErrorResponse(response)) {
                throw response;
            }

            const responseMessage = response.data;

            this.dom.renameFolderPopupFields.englishNameInput.value = responseMessage.folderNameEn;
            this.dom.renameFolderPopupFields.welshNameInput.value = responseMessage.folderNameCy;

        } catch (error) {
            validationSummary(
                {
                    body: "",
                    level: "error",
                    title: await getErrorMessage(error)
                }
            );
        }
    }
}
